import craftsmanship from '../shared/craftsmanship';
import { useEffect, useState } from 'react';
import CharacterVitals from './CharacterVitals.js';
import MediaImage from './MediaImage';
import ContractTerms from './ContractTerms.js';
import { isDepletedProvision } from '../shared/inventoryQuantity';
import { projectInventoryContainment } from '../shared/inventoryContainment';

const STAT_FIELDS = [
  { key: 'strength', label: 'Strength' },
  { key: 'agility', label: 'Agility' },
  { key: 'constitution', label: 'Constitution' },
  { key: 'intellect', label: 'Intellect' },
  { key: 'wisdom', label: 'Wisdom' },
  { key: 'charisma', label: 'Charisma' },
];

const STAT_MIN = -5;
const STAT_MAX = 30;
const STAT_POINT_TOTAL = 75;
const PRIMARY_SKILL_MIN = 0;
const PRIMARY_SKILL_MAX = 4;
const PRIMARY_SKILL_POINT_TOTAL = 18;
const PRIMARY_ABILITY_DESCRIPTIONS = {
  1: 'Adds 5 maximum HP per rank; improves resistance to damage, poison, and disease',
  2: 'Improves fighting and defense',
  3: 'Improves movement, travel, and endurance',
  4: 'Improves stalking, hiding, and trap understanding',
  5: 'Improves observation and attention to detail',
  6: 'Improves ability to acquire, attune, and cast spells',
  7: 'Improves ability to focus, heal, and sleep',
  8: 'Improves knowledge of the world',
  9: 'Improves ability to inspire, convince, and barter',
};

const CONTRACT_FILTERS = [
  { key: 'active', label: 'Active' },
  { key: 'stopped', label: 'Stopped' },
  { key: 'all', label: 'All' },
];

const CHARACTER_SHEET_TAB_STORAGE_KEY = 'adenrel.characterSheet.activeTab';
const INVENTORY_TAB_STORAGE_KEY = 'adenrel.characterSheet.activeInventoryTab';
const CHARACTER_SHEET_TABS = [
  { key: 'info', label: 'Info' },
  { key: 'stats', label: 'Stats & Skills' },
  { key: 'inventory', label: 'Inventory' },
  { key: 'spells', label: 'Spells' },
  { key: 'contracts', label: 'Contracts' },
];
const CHARACTER_SHEET_TAB_KEYS = new Set(CHARACTER_SHEET_TABS.map((tab) => tab.key));
const INVENTORY_TABS = [
  { key: 'armor-clothing', label: 'Armor and Clothing' },
  { key: 'weapons', label: 'Weapons' },
  { key: 'containers', label: 'Containers' },
  { key: 'general-items', label: 'General' },
  { key: 'provisions', label: 'Provisions' },
  { key: 'currency', label: 'Currency' },
];
const INVENTORY_TAB_KEYS = new Set(INVENTORY_TABS.map((tab) => tab.key));

const STOPPED_CONTRACT_STATUS_IDS = new Set([12105, 12106, 12107]);
const STOPPED_CONTRACT_STATUS_NAMES = new Set(['completed', 'cancelled', 'canceled', 'broken']);

const numberValue = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hasDisplayValue = (value) => value !== null && value !== undefined && String(value).trim() !== '';

const readStoredCharacterSheetTab = () => {
  if (typeof window === 'undefined') {
    return 'info';
  }

  try {
    const storedTab = window.localStorage.getItem(CHARACTER_SHEET_TAB_STORAGE_KEY);
    return CHARACTER_SHEET_TAB_KEYS.has(storedTab) ? storedTab : 'info';
  } catch {
    return 'info';
  }
};

const readStoredInventoryTab = () => {
  if (typeof window === 'undefined') {
    return 'armor-clothing';
  }

  try {
    const storedTab = window.localStorage.getItem(INVENTORY_TAB_STORAGE_KEY);
    return INVENTORY_TAB_KEYS.has(storedTab) ? storedTab : 'armor-clothing';
  } catch {
    return 'armor-clothing';
  }
};

const getItemTypeKey = (item) => String(item?.itemType ?? item?.itemTypeName ?? '').trim().toLowerCase();

const getItemDisplayName = (item) => item?.name || item?.entityName || item?.title || item?.entityTitle || '(Unnamed Item)';

const getItemKey = (item, index) => String(item?.idEntity || item?.name || item?.entityName || `item-${index}`);

const getItemDescription = (item) => item?.description || item?.entityDesc || '';

const getItemSubtypeLabel = (item) =>
  item?.weaponName ||
  item?.weapon ||
  item?.wornName ||
  item?.worn ||
  item?.provisionMaterialName ||
  item?.provisionMaterial ||
  item?.containerName ||
  item?.container ||
  item?.generalName ||
  item?.general ||
  '';

const getItemSortLevel = (item) => {
  const parsed = Number(item?.level);
  return Number.isFinite(parsed) ? parsed : -1;
};

const sortItemsByLevelThenName = (items) =>
  [...items].sort((left, right) => {
    const levelSort = getItemSortLevel(right) - getItemSortLevel(left);
    if (levelSort !== 0) {
      return levelSort;
    }
    return getItemDisplayName(left).localeCompare(getItemDisplayName(right));
  });

const isWeaponItem = (item) =>
  getItemTypeKey(item) === 'weapon' ||
  Boolean(item?.weapon || item?.weaponName || item?.idWeapon);

const isWornItem = (item) =>
  getItemTypeKey(item) === 'worn' ||
  Boolean(item?.worn || item?.wornName || item?.idWorn);

const isContainerItem = (item) =>
  getItemTypeKey(item) === 'container' ||
  Boolean(item?.container || item?.containerName || item?.idContainer);

const isProvisionItem = (item) =>
  getItemTypeKey(item) === 'provision' ||
  Boolean(item?.provisionMaterial || item?.provisionMaterialName || item?.idProvisionMaterial);

const buildOwnedItemCategories = (ownedItems) => {
  const categories = [
    { key: 'weapons', label: 'Weapons', items: [] },
    { key: 'armor-clothing', label: 'Armor and Clothing', items: [] },
    { key: 'containers', label: 'Containers', items: [] },
    { key: 'provisions', label: 'Provisions', items: [] },
    { key: 'general-items', label: 'General Items', items: [] },
  ];

  for (const item of ownedItems) {
    if (isWeaponItem(item)) {
      categories[0].items.push(item);
    } else if (isWornItem(item)) {
      categories[1].items.push(item);
    } else if (isContainerItem(item)) {
      categories[2].items.push(item);
    } else if (isProvisionItem(item)) {
      categories[3].items.push(item);
    } else {
      categories[4].items.push(item);
    }
  }

  return categories.map((category) => ({
    ...category,
    items: sortItemsByLevelThenName(category.items),
  }));
};

const parseTermsJson = (contract) => {
  if (!contract?.termsJson) {
    return {};
  }
  if (typeof contract.termsJson === 'object') {
    return contract.termsJson;
  }
  try {
    return JSON.parse(contract.termsJson) || {};
  } catch {
    return {};
  }
};

const getContractName = (contract) =>
  contract?.entityName || contract?.name || contract?.entityTitle || contract?.title || contract?.contractTypeName || 'Contract';

const getContractDescription = (contract) => {
  const terms = parseTermsJson(contract);
  return (
    contract?.entityDesc ||
    contract?.description ||
    terms.description ||
    terms.summary ||
    terms.requestedItemDescription ||
    contract?.contractTypeName ||
    ''
  );
};

const isStoppedContract = (contract) => {
  const statusName = String(contract?.statusName ?? '').trim().toLowerCase();
  const statusId = Number(contract?.status);
  return STOPPED_CONTRACT_STATUS_NAMES.has(statusName) || STOPPED_CONTRACT_STATUS_IDS.has(statusId);
};

const formatContractRoleLabel = (role) => {
  const normalizedRole = String(role ?? '').trim();
  return normalizedRole.toLowerCase() === 'work site' ? 'Site' : normalizedRole || 'Related';
};

const formatRoleList = (relationships = []) => {
  if (!Array.isArray(relationships) || relationships.length === 0) {
    return '';
  }
  const grouped = new Map();
  for (const relationship of relationships) {
    const name = relationship?.isPlayerCharacter
      ? 'You'
      : relationship?.entityName || relationship?.entityTitle || 'Unknown';
    const roles = grouped.get(name) ?? new Set();
    roles.add(formatContractRoleLabel(relationship?.role));
    grouped.set(name, roles);
  }
  return [...grouped.entries()]
    .map(([name, roles]) => `${name}: ${[...roles].join(', ')}`)
    .join('; ');
};

const formatContractRoles = (contract) => {
  const pcRoles = formatRoleList(contract?.pcRoles);
  const otherRoles = formatRoleList(contract?.otherRoles);
  return [pcRoles, otherRoles].filter(Boolean).join('; ') || 'No roles recorded.';
};

function CharacterSheet({
  value,
  currentGameMinute,
  vitals,
  onChange,
  onSave,
  mode = 'edit',
  canSaveCharacter = false,
  isLoading = false,
  isSaving = false,
  errorMessage = '',
  statusMessage = '',
  masterTypes = [],
  spellListOptions = [],
  currencyOptions = [],
  primarySkillOptions = [],
  portrait = {},
  canGeneratePortrait = false,
  canSavePortrait = false,
  isGeneratingDescription = false,
  onGenerateDescription,
  onGeneratePortrait,
  onSavePortrait,
  onContinue,
  initialTab,
  advancement,
  progression,
}) {
  const [contractFilter, setContractFilter] = useState('active');
  const [activeSheetTab, setActiveSheetTab] = useState(() => CHARACTER_SHEET_TAB_KEYS.has(initialTab) ? initialTab : readStoredCharacterSheetTab());
  const [activeInventoryTab, setActiveInventoryTab] = useState(readStoredInventoryTab);
  const [expandedItemKeys, setExpandedItemKeys] = useState(() => new Set());
  useEffect(() => { if (CHARACTER_SHEET_TAB_KEYS.has(initialTab)) setActiveSheetTab(initialTab); }, [initialTab]);
  const character = value ?? {};
  const isPlayMode = mode === 'play';
  const canEditCharacter = !isPlayMode;
  const primarySkills = Array.isArray(character.primarySkills) ? character.primarySkills : [];
  const inventoryItems = Array.isArray(character.ownedItems) ? character.ownedItems : [];
  const carriedItems = (isPlayMode ? projectInventoryContainment(inventoryItems, character.idEntity) : inventoryItems)
    .filter(item => !isPlayMode || !isDepletedProvision(item));
  const borrowedItems = carriedItems.filter((item) => item?.borrowed === true || (item?.physicallyCarried === true && item?.legalOwner === false));
  const ownedItems = carriedItems.filter((item) => !borrowedItems.includes(item));
  const ownedItemCategories = buildOwnedItemCategories(ownedItems);
  const displayedBorrowedItems = buildOwnedItemCategories(borrowedItems).find(category => category.key === activeInventoryTab)?.items ?? [];
  const contracts = Array.isArray(character.contracts) ? character.contracts : [];
  const characterSpells = Array.isArray(character.spells) ? character.spells : [];
  const currencies = Array.isArray(character.currencies) ? character.currencies : [];
  const displayedContracts = contracts.filter((contract) => {
    if (contractFilter === 'all') {
      return true;
    }
    const stopped = isStoppedContract(contract);
    return contractFilter === 'stopped' ? stopped : !stopped;
  });

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(CHARACTER_SHEET_TAB_STORAGE_KEY, activeSheetTab);
    } catch {
      // Ignore storage failures; the visible tab still works for the current session.
    }
  }, [activeSheetTab]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(INVENTORY_TAB_STORAGE_KEY, activeInventoryTab);
    } catch {
      // Ignore storage failures; the visible tab still works for the current session.
    }
  }, [activeInventoryTab]);

  const selectSheetTab = (tabKey) => {
    if (CHARACTER_SHEET_TAB_KEYS.has(tabKey)) {
      setActiveSheetTab(tabKey);
    }
  };

  const toggleExpandedItem = (itemKey) => {
    setExpandedItemKeys((currentKeys) => {
      const nextKeys = new Set(currentKeys);
      if (nextKeys.has(itemKey)) {
        nextKeys.delete(itemKey);
      } else {
        nextKeys.add(itemKey);
      }
      return nextKeys;
    });
  };

  const formatPhysicalStatus = (item) => {
    if (item?.inventoryContainerLabel) return `In ${item.inventoryContainerLabel}`;
    const physicalState = String(item?.physicalState ?? '').trim();
    if (item?.physicallyCarried === true || physicalState === 'carried' || physicalState === 'legacy_or_owned') {
      return 'Carried';
    }
    if (physicalState === 'loose' || item?.looseLocation) {
      const location = item?.looseLocation ?? item?.location ?? {};
      const placeName = item?.loosePlaceName || location.placeName || item?.locationLabel || '';
      return placeName ? `Loose in ${placeName}` : 'Loose in world';
    }
    if (physicalState === 'held_elsewhere' || physicalState === 'contained_elsewhere') {
      const holder = item?.physicalHolderName || item?.physicalHolderTitle;
      return holder ? `With ${holder}` : 'Held elsewhere';
    }
    if (item?.physicallyCarried === false || physicalState === 'not_carried') {
      return 'Not carried';
    }
    return '';
  };

  const formatItemDimensions = (item) => {
    const dimensions = [
      hasDisplayValue(item.length) ? `L ${item.length}` : '',
      hasDisplayValue(item.width) ? `W ${item.width}` : '',
      hasDisplayValue(item.height) ? `H ${item.height}` : '',
    ].filter(Boolean);
    return dimensions.join(' x ');
  };

  const getItemSummaryChips = (item) => {
    const level = Number(item?.level);
    const spellCount = Array.isArray(item?.spells) ? item.spells.length : 0;
    return [
      getItemTypeKey(item) ? item.itemType || item.itemTypeName : '',
      getItemSubtypeLabel(item),
      Number.isFinite(level) ? `Level ${level}` : '',
      formatPhysicalStatus(item),
      spellCount > 0 ? `${spellCount} spell${spellCount === 1 ? '' : 's'}` : '',
    ].filter(Boolean);
  };

  const getItemDetailRows = (item) => [
    ['Type', [item?.itemType || item?.itemTypeName, getItemSubtypeLabel(item)].filter(Boolean).join(' - ')],
    ['Level', item?.level],
    ['Quality', craftsmanship.formatQuality(item?.qualityLevel)],
    ['Magic', item?.magicLevel],
    ['Value', item?.value],
    ['Weight', item?.weight],
    ['Dimensions', formatItemDimensions(item)],
    ['Volume', hasDisplayValue(item?.volume) || hasDisplayValue(item?.capacity)
      ? `${hasDisplayValue(item?.volume) ? item.volume : 0}${hasDisplayValue(item?.capacity) ? ` / ${item.capacity}` : ''}`
      : ''],
    ['Physical', formatPhysicalStatus(item)],
    ['Legal Owner', item?.legalOwnerName || item?.legalOwnerTitle || item?.ownerName || item?.ownerTitle],
    ['Contained By', item?.containedByName || item?.containedByTitle],
    ['Held With', item?.heldWithBodyPart],
    ['Carried With', item?.carriedWithBodyPart],
    ['Quantity', item?.quantity],
    ['Prepared Type', item?.preparedType],
  ].filter(([, detailValue]) => hasDisplayValue(detailValue));

  const renderItemSpells = (item) => {
    const itemSpells = Array.isArray(item?.spells) ? item.spells : [];
    if (itemSpells.length === 0) {
      return <div className="character-empty-state">No item spells.</div>;
    }

    return (
      <div className="character-inventory-spell-list">
        <div className="character-inventory-spell-row character-inventory-spell-head">
          <span>Spell List</span>
          <span>Spell Usage</span>
          <span>Qty</span>
          <span>Level</span>
          <span>Realm</span>
          <span>Acquired Via</span>
          <span>Notes</span>
        </div>
        {itemSpells.map((spell, index) => (
          <div key={`inventory-item-spell-${spell.idSpell || index}`} className="character-inventory-spell-row">
            <span>{spell.idSpellList || spell.name || 'Spell'}</span>
            <span>{spell.idSpellUsage || ''}</span>
            <span>{hasDisplayValue(spell.quantity) ? spell.quantity : ''}</span>
            <span>{hasDisplayValue(spell.level) ? spell.level : ''}</span>
            <span>{spell.realm || ''}</span>
            <span>{spell.acquiredVia || ''}</span>
            <span>{spell.acquiredNotes || ''}</span>
          </div>
        ))}
      </div>
    );
  };

  const renderExpandedItemDetails = (item) => {
    const detailRows = getItemDetailRows(item);
    const description = getItemDescription(item);
    const materials = Array.isArray(item?.materials) ? item.materials.filter(hasDisplayValue) : [];
    const containedItems = (Array.isArray(item?.containsItems) ? item.containsItems : [])
      .filter(containedItem => !isPlayMode || !isDepletedProvision(containedItem));

    return (
      <div className="character-inventory-details">
        <section className="character-inventory-detail-section">
          <h4>Description</h4>
          {description ? <p>{description}</p> : <div className="character-empty-state">No description.</div>}
        </section>

        {detailRows.length > 0 && (
          <section className="character-inventory-detail-section">
            <h4>Details</h4>
            <dl className="character-inventory-detail-grid">
              {detailRows.map(([label, detailValue]) => (
                <div key={`item-detail-${label}`}>
                  <dt>{label}</dt>
                  <dd>{detailValue}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        {materials.length > 0 && (
          <section className="character-inventory-detail-section">
            <h4>Materials</h4>
            <div className="character-inventory-chip-list">
              {materials.map((material, index) => (
                <span key={`item-material-${material}-${index}`}>{material}</span>
              ))}
            </div>
          </section>
        )}

        {containedItems.length > 0 && (
          <section className="character-inventory-detail-section">
            <h4>Contains</h4>
            <div className="character-inventory-chip-list">
              {containedItems.map((containedItem, index) => (
                <span key={`item-contained-${containedItem?.idEntity || index}`}>
                  {getItemDisplayName(containedItem)}
                </span>
              ))}
            </div>
          </section>
        )}

        <section className="character-inventory-detail-section">
          <h4>Spells</h4>
          {renderItemSpells(item)}
        </section>
      </div>
    );
  };

  const getMasterTypeOptions = (column) =>
    masterTypes
      .filter((masterType) => masterType.column === column)
      .map((masterType) => ({
        idMasterType: masterType.idMasterType,
        value: masterType.value,
        label: masterType.value,
      }));

  const allowedBeingTypeOrder = ['elf', 'dwarf', 'human', 'halfling', 'gnome'];
  const characterBeingTypeOptions = getMasterTypeOptions('beingType')
    .filter((option) => allowedBeingTypeOrder.includes(String(option.value ?? '').trim().toLowerCase()))
    .sort(
      (left, right) =>
        allowedBeingTypeOrder.indexOf(String(left.value ?? '').trim().toLowerCase()) -
        allowedBeingTypeOrder.indexOf(String(right.value ?? '').trim().toLowerCase())
    );

  const applyCharacterPatch = (patch) => {
    if (typeof onChange !== 'function') {
      return;
    }

    onChange({
      ...character,
      ...patch,
      entityType: 'Being',
      level: character.level ?? 1,
      defaultForm: patch.beingType ?? patch.defaultForm ?? character.defaultForm ?? '',
      multipleForms: false,
      personality: '',
      objectivesShortTerm: '',
      objectivesLongTerm: '',
      forms: [],
      relationships: [],
      itemSuggestions: [],
    });
  };

  const updateCharacter = (patch) => {
    if (!canEditCharacter) {
      return;
    }

    applyCharacterPatch(patch);
  };

  const updatePointAllocation = (patch) => {
    applyCharacterPatch(patch);
  };

  const updateField = (field, nextValue) => {
    updateCharacter({ [field]: nextValue });
  };

  const updateBeingType = (nextValue) => {
    updateCharacter({ beingType: nextValue, defaultForm: nextValue });
  };

  const statTotal = STAT_FIELDS.reduce((total, stat) => total + numberValue(character[stat.key]), 0);
  const statRemaining = STAT_POINT_TOTAL - statTotal;
  const canSpendAdditionalStatPoints = isPlayMode && statRemaining > 0;
  const canDecreaseAssignedPoints = canEditCharacter && !character.idEntity;
  const canIncreaseStats = canEditCharacter || canSpendAdditionalStatPoints;

  const adjustStat = (field, delta) => {
    if (isPlayMode && delta < 0) {
      return;
    }

    if (isPlayMode && delta > 0 && statRemaining <= 0) {
      return;
    }

    const current = numberValue(character[field]);
    const next = current + delta;

    if (next < STAT_MIN || next > STAT_MAX) {
      return;
    }

    if (delta > 0 && statTotal >= STAT_POINT_TOTAL) {
      return;
    }

    updatePointAllocation({ [field]: next });
  };

  const primarySkillTotal = primarySkills.reduce((total, skill) => total + numberValue(skill.level), 0);
  const primarySkillRemaining = Math.max(0, PRIMARY_SKILL_POINT_TOTAL - primarySkillTotal);
  const canSpendAdditionalPrimarySkillPoints = isPlayMode && !vitals?.skillProgression && !character.skillProgression && primarySkillRemaining > 0;
  const canIncreasePrimarySkills = canEditCharacter || canSpendAdditionalPrimarySkillPoints;

  const adjustPrimarySkill = (idPrimarySkillList, delta) => {
    if (isPlayMode && delta < 0) {
      return;
    }

    if (isPlayMode && delta > 0 && primarySkillRemaining <= 0) {
      return;
    }

    const nextSkills = primarySkillOptions.map((skillOption) => {
      const existing = primarySkills.find(
        (skill) => Number(skill.idPrimarySkillList) === Number(skillOption.idPrimarySkillList)
      );
      const currentLevel = numberValue(existing?.level);
      const nextLevel =
        Number(skillOption.idPrimarySkillList) === Number(idPrimarySkillList)
          ? currentLevel + delta
          : currentLevel;

      return {
        idPrimarySkill: existing?.idPrimarySkill ?? '',
        idPrimarySkillList: skillOption.idPrimarySkillList,
        name: skillOption.name,
        stat: skillOption.stat,
        description: skillOption.description ?? '',
        gmNotes: skillOption.gmNotes ?? '',
        level: Math.min(PRIMARY_SKILL_MAX, Math.max(PRIMARY_SKILL_MIN, nextLevel)),
      };
    });

    const nextTotal = nextSkills.reduce((total, skill) => total + numberValue(skill.level), 0);
    if (nextTotal > PRIMARY_SKILL_POINT_TOTAL) {
      return;
    }

    updatePointAllocation({ primarySkills: nextSkills });
  };

  const displaySkillRows = primarySkillOptions.map((skillOption) => {
    const existing = primarySkills.find(
      (skill) => Number(skill.idPrimarySkillList) === Number(skillOption.idPrimarySkillList)
    );

    return {
      ...skillOption,
      level: numberValue(existing?.level),
    };
  });

  const normalizeDisplayLookup = (value) => String(value ?? '').trim().toLowerCase();

  const findSpellListOption = (spell) => {
    const spellCandidates = [
      spell?.idSpellList,
      spell?.spellList,
      spell?.spellListName,
      spell?.name,
      spell?.spellName,
    ]
      .map(normalizeDisplayLookup)
      .filter(Boolean);

    return spellListOptions.find((option) =>
      [option?.value, option?.idSpellList, option?.name, option?.label]
        .map(normalizeDisplayLookup)
        .some((optionValue) => optionValue && spellCandidates.includes(optionValue))
    );
  };

  const getSpellTypeLabel = (spell) => {
    const spellListOption = findSpellListOption(spell);
    return spellListOption?.label || spell?.spellListName || spell?.spellName || spell?.name || spell?.idSpellList || 'Spell';
  };

  const getSpellGuidance = (spell) => {
    const spellListOption = findSpellListOption(spell);
    return spell?.description || spellListOption?.description || '';
  };

  const getMasterTypeLabel = (column, value) => {
    const normalizedValue = normalizeDisplayLookup(value);
    const option = getMasterTypeOptions(column).find(
      (candidate) =>
        normalizeDisplayLookup(candidate.idMasterType) === normalizedValue ||
        normalizeDisplayLookup(candidate.value) === normalizedValue ||
        normalizeDisplayLookup(candidate.label) === normalizedValue
    );
    return option?.label || value || '';
  };

  const formatSpellAcquiredNotes = (value) =>
    String(value ?? '')
      .replace(/through\s+Play2\s+spell\s+acquisition/gi, 'through spell acquisition')
      .replace(/\bPlay2\s+spell\s+acquisition\b/gi, 'spell acquisition');

  const getCurrencyLabel = (currency) =>
    currencyOptions.find((option) => String(option.value) === String(currency?.idCurrency))?.label ??
    currency?.currencyName ??
    currency?.name ??
    'Currency';

  const renderItemList = (items, emptyLabel) =>
    items.length > 0 ? (
      <div className="character-inventory-list">
        {items.map((item, index) => {
          const itemKey = getItemKey(item, index);
          const isExpanded = expandedItemKeys.has(itemKey);
          const summaryChips = getItemSummaryChips(item);

          return (
            <div key={itemKey} className={`character-inventory-item${isExpanded ? ' expanded' : ''}`}>
              <button
                type="button"
                className="character-inventory-item-toggle"
                onClick={() => toggleExpandedItem(itemKey)}
                aria-expanded={isExpanded}
              >
                <span className="character-inventory-item-main">
                  <strong>{getItemDisplayName(item)}</strong>
                  {(item.title || item.entityTitle) && <em>{item.title || item.entityTitle}</em>}
                </span>
                <span className="character-inventory-item-summary">
                  {summaryChips.length > 0 ? summaryChips.map((chip) => <span key={`${itemKey}-${chip}`}>{chip}</span>) : <span>Item</span>}
                </span>
                <span className="character-inventory-expand-label">{isExpanded ? 'Collapse' : 'Expand'}</span>
              </button>
              {isExpanded && renderExpandedItemDetails(item)}
            </div>
          );
        })}
      </div>
    ) : (
      <div className="character-empty-state">{emptyLabel}</div>
    );

  return (
    <main className="character-sheet-page">
      <section className="character-sheet-shell">
        <header className="character-sheet-header">
          <div>
            <h1>Character Sheet</h1>
            {!isPlayMode && <p>{character.idEntity ? 'Loaded character' : 'New character draft'}</p>}
          </div>
          <div className="character-level-pill">Level {numberValue(progression?.phase === 'allocationRequired'
            ? progression.transition?.nextLevel : character.level, 1) || 1}</div>
        </header>

        <CharacterVitals being={vitals ?? character} />

        {isLoading && <div className="character-sheet-status">Loading character...</div>}
        {statusMessage && <div className="character-sheet-status">{statusMessage}</div>}
        {errorMessage && <div className="character-sheet-status character-sheet-error">{errorMessage}</div>}

        <nav className="character-sheet-tabs" aria-label="Character sheet sections">
          {CHARACTER_SHEET_TABS.map((tab) => (
            <button
              key={`character-sheet-tab-${tab.key}`}
              type="button"
              className={`character-sheet-tab${activeSheetTab === tab.key ? ' active' : ''}`}
              onClick={() => selectSheetTab(tab.key)}
              aria-current={activeSheetTab === tab.key ? 'page' : undefined}
            >
              {tab.label}
            </button>
          ))}
        </nav>

        {activeSheetTab === 'info' && (
        <div className="character-profile-grid">
          <section className="character-sheet-section character-information-section">
            <h2>Information</h2>
            <div className="character-sheet-grid">
              <label>
                <span>Name</span>
                <input
                  value={character.name ?? ''}
                  placeholder="Required"
                  required
                  readOnly={!canEditCharacter}
                  onChange={(event) => updateField('name', event.target.value)}
                />
              </label>
              <label>
                <span>Form</span>
                <select
                  required
                  value={character.beingType ?? ''}
                  onChange={(event) => updateBeingType(event.target.value)}
                  disabled={!canEditCharacter}
                >
                  <option value="">Select...</option>
                  {characterBeingTypeOptions.map((option) => (
                    <option key={`character-being-type-${option.value}`} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                <span>Heraldry</span>
                <input
                  value={character.heraldry ?? ''}
                  placeholder="Optional"
                  readOnly={!canEditCharacter}
                  onChange={(event) => updateField('heraldry', event.target.value)}
                />
              </label>
            </div>
            <label className="character-sheet-wide-field">
              <span className="character-field-label-row">
                <span>Description</span>
                {canEditCharacter && (
                  <button
                    type="button"
                    className="character-inline-button"
                    onClick={onGenerateDescription}
                    disabled={isLoading || isSaving || isGeneratingDescription || typeof onGenerateDescription !== 'function'}
                  >
                    {isGeneratingDescription ? 'Generating...' : 'Generate Description'}
                  </button>
                )}
              </span>
              <textarea
                value={character.description ?? ''}
                placeholder="Required"
                required
                readOnly={!canEditCharacter}
                onChange={(event) => updateField('description', event.target.value)}
              />
            </label>
          </section>

          <aside className="character-portrait-column">
            <div className="character-portrait-panel character-portrait-card">
              <div className="character-portrait-frame">
                {portrait.src ? (
                  <MediaImage src={portrait.src} alt="Character portrait" size="display" />
                ) : (
                  <span>{character.idEntity ? 'No portrait saved' : 'Save character first'}</span>
                )}
              </div>
              {canEditCharacter && (
                <div className="character-portrait-actions">
                  <button
                    type="button"
                    onClick={onGeneratePortrait}
                    disabled={!canGeneratePortrait || portrait.isGenerating || portrait.isSaving}
                  >
                    {portrait.isGenerating ? 'Generating...' : 'Generate Image'}
                  </button>
                  <button
                    type="button"
                    onClick={onSavePortrait}
                    disabled={!canSavePortrait || portrait.isGenerating || portrait.isSaving}
                  >
                    {portrait.isSaving ? 'Saving...' : 'Save Image'}
                  </button>
                </div>
              )}
              {portrait.error && <div className="character-portrait-error">{portrait.error}</div>}
            </div>

            {canEditCharacter && (
              <div className="character-portrait-footer-actions">
                <button
                  type="button"
                  className="character-save-button"
                  onClick={onSave}
                  disabled={isLoading || isSaving || !canSaveCharacter}
                >
                  {isSaving ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  className="character-continue-button"
                  onClick={onContinue}
                  disabled={isLoading || isSaving || !character.idEntity || !portrait.isSaved || typeof onContinue !== 'function'}
                >
                  Continue
                </button>
              </div>
            )}
          </aside>
        </div>
        )}

        {activeSheetTab === 'stats' && (
        advancement || <>
        <section className="character-sheet-section">
          <div className="character-sheet-section-header">
            <h2>Stats</h2>
            <span>{statRemaining} points remaining</span>
          </div>
          <div className="character-point-grid">
            {STAT_FIELDS.map((stat) => {
              const current = numberValue(character[stat.key]);
              const rowClassName = [
                'character-point-row',
                canDecreaseAssignedPoints ? '' : 'character-point-row-no-decrease',
                canIncreaseStats ? '' : 'character-point-row-readonly',
              ].filter(Boolean).join(' ');
              return (
                <div key={stat.key} className={rowClassName}>
                  <span>{stat.label}</span>
                  {canDecreaseAssignedPoints && (
                    <button type="button" onClick={() => adjustStat(stat.key, -1)} disabled={current <= STAT_MIN}>
                      -
                    </button>
                  )}
                  <strong>{current}</strong>
                  {canIncreaseStats && (
                    <button
                      type="button"
                      onClick={() => adjustStat(stat.key, 1)}
                      disabled={current >= STAT_MAX || statTotal >= STAT_POINT_TOTAL}
                    >
                      +
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="character-sheet-section">
          <div className="character-sheet-section-header">
            <h2>Primary Abilities</h2>
            <span>{primarySkillRemaining} points remaining</span>
          </div>
          <div className="character-point-grid">
            {displaySkillRows.map((skill) => {
              const rowClassName = [
                'character-point-row',
                canDecreaseAssignedPoints ? '' : 'character-point-row-no-decrease',
                canIncreasePrimarySkills ? '' : 'character-point-row-readonly',
              ].filter(Boolean).join(' ');
              return (
                <div key={skill.idPrimarySkillList} className={rowClassName}>
                  <span className="character-point-label">
                    <span>{skill.name}</span>
                    <em>{PRIMARY_ABILITY_DESCRIPTIONS[Number(skill.idPrimarySkillList)]}</em>
                  </span>
                  {canDecreaseAssignedPoints && (
                    <button
                      type="button"
                      onClick={() => adjustPrimarySkill(skill.idPrimarySkillList, -1)}
                      disabled={skill.level <= PRIMARY_SKILL_MIN}
                    >
                      -
                    </button>
                  )}
                  <strong>{skill.level}</strong>
                  {canIncreasePrimarySkills && (
                    <button
                      type="button"
                      onClick={() => adjustPrimarySkill(skill.idPrimarySkillList, 1)}
                      disabled={skill.level >= PRIMARY_SKILL_MAX || primarySkillTotal >= PRIMARY_SKILL_POINT_TOTAL}
                    >
                      +
                    </button>
                  )}
                </div>
              );
            })}
            {displaySkillRows.length === 0 && <div className="character-empty-state">No primary skills loaded.</div>}
          </div>
        </section>
        </>
        )}

        {activeSheetTab === 'spells' && (
        <section className="character-sheet-section">
          <h2>Spells</h2>
          <div className="character-list">
            {characterSpells.length > 0 && (
              <div className="character-spell-row character-spell-row-header">
                <span>Name</span>
                <span>Level</span>
                <span>Realm</span>
                <span>Acquired Via</span>
                <span>Notes</span>
              </div>
            )}
            {characterSpells.map((spell, index) => {
              const spellTypeLabel = getSpellTypeLabel(spell);
              const spellGuidance = getSpellGuidance(spell);
              return (
                <div key={`character-spell-${spell.idSpell || index}`} className="character-spell-row">
                  <span className="character-spell-type-cell">
                    <span
                      className={`character-spell-type-name${spellGuidance ? ' has-guidance' : ''}`}
                      tabIndex={spellGuidance ? 0 : undefined}
                    >
                      {spellTypeLabel}
                    </span>
                    {spellGuidance && (
                      <span className="character-spell-guidance" role="tooltip">
                        {spellGuidance}
                      </span>
                    )}
                  </span>
                  <strong>{hasDisplayValue(spell.level) ? spell.level : '-'}</strong>
                  <span>{getMasterTypeLabel('realm', spell.realm) || '-'}</span>
                  <span>{getMasterTypeLabel('acquiredVia', spell.acquiredVia) || '-'}</span>
                  <span>{spell.available === false ? (spell.divineStatus === 'unverified' ? 'Patron unverified. ' : 'Power withheld. ') : ''}{formatSpellAcquiredNotes(spell.acquiredNotes) || '-'}</span>
                </div>
              );
            })}
            {characterSpells.length === 0 && <div className="character-empty-state">No spells.</div>}
          </div>
        </section>
        )}

        {activeSheetTab === 'inventory' && (
        <>
        <nav className="character-inventory-tabs" aria-label="Inventory categories">
          {INVENTORY_TABS.map(tab => (
            <button key={tab.key} type="button"
              className={`character-sheet-tab${activeInventoryTab === tab.key ? ' active' : ''}`}
              aria-current={activeInventoryTab === tab.key ? 'page' : undefined}
              onClick={() => setActiveInventoryTab(tab.key)}>{tab.label}</button>
          ))}
        </nav>
        {activeInventoryTab !== 'currency' && (
        <section className="character-sheet-section">
          <h2>Owned Items</h2>
          <div className="character-item-category-grid">
            {ownedItemCategories.filter(category => category.key === activeInventoryTab).map((category) => (
              <section key={category.key} className="character-item-category">
                <h3>{category.label}</h3>
                {renderItemList(category.items, `No ${category.label.toLowerCase()}.`)}
              </section>
            ))}
          </div>
        </section>
        )}

        {displayedBorrowedItems.length > 0 && (
          <section className="character-sheet-section">
            <h2>Borrowed / Not Yours</h2>
            {renderItemList(displayedBorrowedItems, 'No borrowed items.')}
          </section>
        )}
        </>
        )}

        {activeSheetTab === 'contracts' && (
          <section className="character-sheet-section">
            <div className="character-sheet-section-header">
              <h2>Contracts</h2>
              <div className="character-contract-filters" role="group" aria-label="Contract filters">
                {CONTRACT_FILTERS.map((filter) => (
                  <button
                    key={`contract-filter-${filter.key}`}
                    type="button"
                    className={`character-contract-filter${contractFilter === filter.key ? ' active' : ''}`}
                    onClick={() => setContractFilter(filter.key)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>
            </div>
            {displayedContracts.length > 0 ? (
              <div className="character-list">
                {displayedContracts.map((contract) => (
                  <details key={contract.idEntity || getContractName(contract)} className="character-list-row character-contract-row">
                    <summary>{getContractName(contract)}</summary>
                    <div className="character-contract-details">
                      <div className="character-contract-description">
                        <p>{getContractDescription(contract)}</p>
                        <ContractTerms details={parseTermsJson(contract)} accepted currentGameMinute={currentGameMinute} />
                      </div>
                      <span>{formatContractRoles(contract)}</span>
                    </div>
                  </details>
                ))}
              </div>
            ) : (
              <div className="character-empty-state">No {contractFilter} contracts.</div>
            )}
          </section>
        )}

        {activeSheetTab === 'inventory' && activeInventoryTab === 'currency' && (
        <section className="character-sheet-section">
          <h2>Currency</h2>
          <div className="character-list">
            {currencies.map((currency, index) => (
              <div key={`character-currency-${currency.idCurrency || index}`} className="character-list-row">
                <span>{getCurrencyLabel(currency)} Pieces</span>
                <strong>{currency.quantity ?? 0}</strong>
              </div>
            ))}
            {currencies.length === 0 && <div className="character-empty-state">No currency.</div>}
          </div>
        </section>
        )}

        {canEditCharacter && (
          <div className="character-sheet-save-row">
            <button
              type="button"
              className="character-save-button"
              onClick={onSave}
              disabled={isLoading || isSaving || !canSaveCharacter}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        )}
      </section>
    </main>
  );
}

export default CharacterSheet;
